// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Import.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, action, computed } from 'mobx';
import ImportFileContent from './Izenda.ImportFileContent';
import ImportFileDatabaseMappingContent from './Izenda.ImportFileDatabaseMappingContent';
import {
  map,
  forEach,
  isEmpty,
  find,
  differenceWith,
  toLower,
  pick,
  filter,
  remove,
  includes,
  flow,
  findIndex,
  findLastIndex,
  isNil
} from 'lodash/fp';
import uiState from 'logic/Izenda.UiState';

export default class Import {
  @observable batchId: string = null;
  @observable importSourceCat: boolean = false;
  @observable importAccessRights: boolean = false;
  @observable importFilterValues: boolean = false;
  @observable reports: Array<ImportFileContent> = [];
  @observable dashboards: Array<ImportFileContent> = [];
  @observable tenantId: string = null;
  @observable hasInvalidFile: boolean = false;
  @observable systemVersion: string = null;
  @observable databaseMappings: Array<ImportFileDatabaseMappingContent> = [];
  invalidFileMessages: Array<string> = [];

  activeTabKeys: Array<any> = [{ key: 'report', index: 1 }, { key: 'dashboard', index: 2 }];
  activeTabKey: string = 'report';

  @computed
  get fileIds() {
    const ids = [];
    const extractIdsFn = collection =>
      collection.reduce((result, { id }) => {
        result.push(id);
        return result;
      }, ids);
    this.reports && extractIdsFn(this.reports);
    this.dashboards && extractIdsFn(this.dashboards);
    return ids;
  }

  /**
   * create new data
   * @param {Object} content
   * @param {String} localCategoryId
   */
  @action
  create(content: Import, localCategoryId: string): void {
    const { reports, dashboards, databaseMappings } = this.populate(content, localCategoryId);
    this.reports = reports;
    this.dashboards = dashboards;
    this.databaseMappings = databaseMappings;
    this.updateActiveTabKey(content);
  }

  /**
   * append file content after browse more files
   * @param {Object} content
   * @param {string} localCategoryId
   * @returns {Array} return a list of report and dashboard content files
   */
  @action
  appendContent(content: Import, localCategoryId: string): any {
    const { reports, dashboards, databaseMappings } = this.populate(content, localCategoryId);

    Array.prototype.push.apply(this.reports, reports);
    Array.prototype.push.apply(this.dashboards, dashboards);
    Array.prototype.push.apply(this.databaseMappings, databaseMappings);

    this.updateActiveTabKey(content);
    return [...reports, ...dashboards];
  }

  /**
   * Update report list
   * @param {String} id
   */
  @action
  updateReports(id: string) {
    let index = findIndex({ id: id }, this.reports);
    index !== -1 && this.reports.splice(index, 1);
  }

  /**
   * Update dashboard list
   * @param {String} id
   */
  @action
  updateDashboards(id: string) {
    const start = findIndex({ id: id }, this.dashboards);
    const end = findLastIndex({ id: id }, this.dashboards);
    this.dashboards.splice(start, end - start + 1);
  }

  /**
   * Populate data
   * @param {Object} content
   * @param {String} localCategoryId
   */
  populate(content: Import, localCategoryId: string) {
    this.batchId = content.batchId;
    this.importAccessRights = content.importAccessRights;
    this.importFilterValues = content.importFilterValues;
    this.tenantId = content.tenantId;
    this.hasInvalidFile = content.hasInvalidFile;
    this.systemVersion = content.systemVersion;
    Array.prototype.push.apply(this.invalidFileMessages, content.invalidFileMessages);

    // Check then get the new database mapping to append
    let databaseMappings = [];
    if (!isEmpty(this.databaseMappings)) {
      const differences = differenceWith(
        (current, toMerge) => {
          return (
            toLower(current.fromDatabaseName) === toLower(toMerge.fromDatabaseName) &&
            toLower(current.fromSchema) === toLower(toMerge.fromSchema) &&
            toLower(current.serverType) === toLower(toMerge.serverType)
          );
        },
        content.databaseMappings,
        this.databaseMappings
      );
      forEach(difference => {
        databaseMappings.push(new ImportFileDatabaseMappingContent(difference));
      }, differences);
    } else {
      // If this.databaseMappings is empty, no need to check each item, push all
      forEach(databaseMapping => {
        databaseMappings.push(new ImportFileDatabaseMappingContent(databaseMapping));
      }, content.databaseMappings);
    }

    // Update index for report list
    for (let i = 0; i < content.reports.length; i++) {
      content.reports[i].content.index = this.reports.length + i;
    }
    const reports = map(report => new ImportFileContent(report, localCategoryId, true), content.reports);

    let dashboards = [];
    for (let i = 0; i < content.dashboards.length; i++) {
      const dashboard = content.dashboards[i];
      dashboard.content.index = this.dashboards.length + dashboards.length;
      dashboards.push(new ImportFileContent(dashboard, localCategoryId, false));

      if (!isEmpty(dashboard.reports)) {
        for (let i = 0; i < dashboard.reports.length; i++) {
          dashboard.reports[i].index = this.dashboards.length + dashboards.length + i;
        }

        Array.prototype.push.apply(
          dashboards,
          map(
            report => new ImportFileContent({ id: dashboard.id, content: report }, localCategoryId, true),
            dashboard.reports
          )
        );
      }
    }

    return { reports: reports, dashboards: dashboards, databaseMappings: databaseMappings };
  }

  /**
   * Update active tab key
   * @param {Object} content
   */
  updateActiveTabKey(content: ImportFileContent) {
    if (!isEmpty(content.reports) && !isEmpty(content.dashboards)) {
      return;
    }

    if (!isEmpty(content.reports)) {
      this.activeTabKey = 'report';
    } else if (!isEmpty(content.dashboards)) {
      this.activeTabKey = 'dashboard';
    } else {
      this.activeTabKey = 'report';
    }
  }

  /**
   * Change active tab key.
   * @param {Number} index
   */
  changeActiveTabKey(index: number) {
    this.activeTabKey = find({ index: index }, this.activeTabKeys).key;
  }

  /**
   * Create object data to validate through APIs
   * @returns {Object}
   */
  buildData(): any {
    let data = {
      batchId: null,
      dashboards: [],
      databaseMappings: [],
      hasInvalidFile: null,
      importSourceCat: null,
      importAccessRights: null,
      importFilterValues: null,
      reports: [],
      systemVersion: null,
      tenantId: null
    };

    data.batchId = this.batchId;
    data.dashboards = this.buildDashboards(this.dashboards);
    data.databaseMappings = this.buildDatabaseMappings(this.databaseMappings);
    data.hasInvalidFile = this.hasInvalidFile;
    data.importAccessRights = this.importAccessRights;
    data.importSourceCat = this.importSourceCat;
    data.importFilterValues = this.importFilterValues;
    data.reports = this.buildReports(this.reports);
    data.systemVersion = this.systemVersion;
    data.tenantId = uiState.tenantId;
    return data;
  }

  /**
   * Create dashboards to validate through APIs
   * @param {Array} dashboardModel
   * @returns {Array}
   */
  buildDashboards(dashboardsModel: Array<ImportFileContent>): Array<any> {
    let dashboards = [];
    forEach(dashboard => {
      if (!dashboard.isReport) {
        let dashboardData = dashboard.buildData();
        dashboardData.reports = [];
        const dashboardReports = filter({ id: dashboard.id, isReport: true }, dashboardsModel);
        forEach(report => {
          dashboardData.reports.push(report.content.buildData());
        }, dashboardReports);
        dashboards.push(dashboardData);
      }
    }, dashboardsModel);
    return dashboards;
  }

  /**
   * Create database mappings to validate through APIs
   * @param {Array} databaseMappingsModel
   * @returns {Array}
   */
  buildDatabaseMappings(databaseMappingsModel: Array<ImportFileContent>): Array<any> {
    let databaseMappings = [];
    forEach(databaseMapping => {
      databaseMappings.push(
        pick(['fromDatabaseName', 'fromSchema', 'serverType', 'toDatabase', 'toSchema'], databaseMapping)
      );
    }, databaseMappingsModel);
    return databaseMappings;
  }

  /**
   * Create reports to validate through APIs
   * @param {Array} reportsModel
   * @returns {Array}
   */
  buildReports(reportsModel: Array<ImportFileContent>): Array<any> {
    let reports = [];
    forEach(report => {
      reports.push(report.buildData());
    }, reportsModel);
    return reports;
  }

  /**
   * Remove completed file
   * @param {Array} importFiles
   */
  @action
  removeCompletedFiles(importFiles: Array<any>) {
    const removeFile = remove(file => includes(file.id, importFiles));

    this.reports = removeFile(this.reports);
    this.dashboards = removeFile(this.dashboards);
  }

  /**
   * Set database mappings
   * @param {Array} databaseMappings
   */
  @action
  updateDatabaseMappings(databaseMappings: Array<any>) {
    this.databaseMappings.replace(
      remove(
        currentMapping =>
          !find(
            newMapping =>
              toLower(currentMapping.fromDatabaseName) === toLower(newMapping.fromDatabaseName) &&
              toLower(currentMapping.fromSchema) === toLower(newMapping.fromSchema) &&
              toLower(currentMapping.serverType) === toLower(newMapping.serverType),
            databaseMappings
          ),
        this.databaseMappings
      )
    );
  }

  /**
   * Update selected to database name
   */
  @action
  updateSelectedToDatabaseName(
    databaseMapping: ImportFileDatabaseMappingContent,
    id: string,
    value: string,
    schemas: Array<any>
  ) {
    const groupIndexs = [];
    databaseMapping.setSelectedToDatabaseName(id, value);
    databaseMapping.toSchemas = schemas;

    this.databaseMappings.forEach((item, index) => {
      if (
        item.fromDatabaseNameLabel === databaseMapping.fromDatabaseNameLabel &&
        item.fromSchema !== databaseMapping.fromSchema
      ) {
        groupIndexs.push(index.toString());

        if (isNil(item.selectedToDatabaseName)) {
          item.setSelectedToDatabaseName(id, value);
          item.toSchemas = schemas;
        }
      }
    });

    return groupIndexs;
  }

  /**
   * Arrange index
   * @param {Number} index
   * @param {Number} step
   */
  @action
  arrangeIndex(index: number, step: number, isDashboardTab: boolean) {
    flow(
      filter(importFileContent => importFileContent.content.index > index + step - 1),
      forEach(importFileContent => {
        importFileContent.content.index = importFileContent.content.index - step;
      })
    )(isDashboardTab ? this.dashboards : this.reports);
  }

   /**
   * Update import category
   */
  toggleImportCategory() {
    this.importSourceCat = !this.importSourceCat;
  }

  /**
   * Update import access rights
   */
  @action
  toggleImportAccessRights() {
    this.importAccessRights = !this.importAccessRights;
  }

  /**
   * Update import the filter values
   */
  @action
  toggleImportTheFilterValues() {
    this.importFilterValues = !this.importFilterValues;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.Import.js